import { motion } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { MotionImage, useSanitizedTranslation, StackedContent, XumoButton } from 'ui';

export interface TrackLogo {
    src: string;
    width: number;
    height: number;
}

export interface CarouselCTA {
    href: string;
    color?: string;
}

export interface CarouselItem {
  logo: TrackLogo;
  date: string;
  month: string;
  day: string;
  trackname: string;
  location: string;
  races: string[];
  cta?: CarouselCTA;
}

export interface CarouselProps {
  bg?: string;
  sectionClass?: string;
  title?: any;
  items: CarouselItem[];
}

const CarouselItem = ({logo, month, day, trackname, location, races, cta}: CarouselItem) => {
    const { t } = useSanitizedTranslation();
    return(
        <div className='w-max min-w-[325px] max-w-[325px] sm:min-w-[400px] sm:max-w-[400px] h-full bg-white rounded-xl flex flex-col items-center justify-center overflow-hidden snap-center'>
            {/* Image */}
            <div className='w-full bg-[#DCE6F0] h-max p-3'>
                <StackedContent
                    center
                    noMargin
                    minHeight
                    content={[
                        {
                            image: {
                                src: logo.src,
                                width: logo.width,
                                height: logo.height,
                                renderedHeight: 150,
                            },
                        },
                    ]}
                />
            </div>
            {/* Text */}
            <div className='w-full flex flex-col items-center justify-center px-6 pt-6 pb-12 gap-8'>
                {/* Date and location */}
                <div className='w-full flex flex-row items-center justify-between gap-4'>
                    <div className='flex flex-col items-center justify-center rounded-sm border border-[#DCE6F0] min-w-[80px] min-h-[80px] sm:min-w-[95px] sm:min-h-[95px] aspect-square p-1'>
                        <p className='uppercase'>{t(month)}</p>
                        <p className='text-xl sm:text-2xl'>{t(day)}</p>
                    </div>
                    <div className='border-l border-black flex flex-col items-center justify-center h-full w-full py-5 pl-3'>
                        <p className='text-center'>{t(trackname)}</p>
                        <p className="text-sm text-center">{t(location)}</p>
                    </div>
                </div>
                {/* Race */}
                <div className='w-3/4 flex flex-col items-center justify-center gap-3'>
                    {races.map((race, index) =>
                        <div className='w-full rounded-full bg-[#F6F8F9] p-3 flex items-center justify-center'>
                            <p key={index + "-race"} className="font-medium text-center">{race}</p>
                        </div>
                    )}
                </div>
                {/* Button */}
                {cta?.href &&
                <XumoButton
                    label="View race recap"
                    href={cta.href}
                    secondary={true}
                    color={cta.color ?? 'xumoTeal'}
                />
                }
            </div>
        </div>
    )
}

export const EventsCarousel = ({bg, title, items, sectionClass=""}: CarouselProps) => {
        const now = new Date();
        const sortedItems = [...items].sort((a, b) => {
            const aDate = new Date(a.date);
            const bDate = new Date(b.date);
            const aIsPast = aDate < now;
            const bIsPast = bDate < now;

            // Different buckets: past goes after upcoming
            if (aIsPast !== bIsPast) {
            return aIsPast ? 1 : -1;
            }

            // Same bucket: sort by date ascending
            return aDate.getTime() - bDate.getTime();
        });

    return(
        <motion.section className={`flex flex-col items-start justify-center ${sectionClass}`} style={{background: bg ? bg : 'white'}}>
        {/* Title */}
            {title ?
                <div className='w-full flex items-center'>
                    <StackedContent 
                        full
                        center
                        content={title} 
                    />
                </div>
                : <div className='py-10'></div>
            }
        {/* Carousel Items */}
            <div className='w-full overflow-x-scroll flex flex-row items-start justify-start gap-5 snap-x p-5 sm:p-10 pb-0 sm:pb-0'>
                {sortedItems.map((item, index) =>
                    <CarouselItem {...item} key={index + "-carouselitem"}/>
                )}
            </div>
        </motion.section>
    )
} 